import { useEffect, useState } from "react";
import { useTheme } from "../App.jsx";
import { API_BASE } from "../lib/auth.js";
import { LANDING_URL } from "../lib/links.js";
import { errorMessage } from "../lib/accountLinks.js";

// Landing pages for the links in account emails (apps/api/email_service.py):
//   /reset-password?token=…  → choose a new password (POST /auth/reset-password)
//   /verify-email?token=…    → confirm the address   (POST /auth/verify-email)
// Rendered by main.jsx at any screen width, before the desktop/phone split,
// because people open these emails on their phones.

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

async function post(path, body) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data };
  } catch {
    return { ok: false, data: { detail: "Couldn't reach the server. Check your connection and try again." } };
  }
}

function Shell({ title, children }) {
  const C = useTheme();
  return (
    <div style={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                  background: C.bg, color: C.text, fontFamily: FONT, padding: "40px 20px" }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <img src="/brand/ftl-mark-small.svg" alt="" width={36} height={36} style={{ marginBottom: 20 }} />
        <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5, margin: "0 0 12px" }}>{title}</h1>
        {children}
      </div>
    </div>
  );
}

function ResetPassword({ token }) {
  const C = useTheme();
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pass !== pass2) { setError("Passwords don't match."); return; }
    setBusy(true); setError("");
    const { ok, data } = await post("/auth/reset-password", { token, new_password: pass });
    setBusy(false);
    if (ok) setDone(true);
    else setError(errorMessage(data, "This reset link is invalid or has expired. Request a new one from the sign-in screen."));
  };

  if (done) {
    return (
      <Shell title="Password updated">
        <p style={{ color: C.textMuted, lineHeight: 1.55 }}>
          Sign in with your new password in the iPhone app or on the web. You've been signed out everywhere else.
        </p>
      </Shell>
    );
  }

  const input = {
    width: "100%", boxSizing: "border-box", padding: "12px 14px", fontSize: 16, borderRadius: 10,
    border: `1px solid ${C.border}`, background: C.surface, color: C.text, marginBottom: 12, fontFamily: FONT,
  };
  return (
    <Shell title="Choose a new password">
      <p style={{ color: C.textMuted, lineHeight: 1.55, margin: "0 0 20px" }}>
        At least 8 characters, with an uppercase letter, a lowercase letter and a number.
      </p>
      <form onSubmit={submit}>
        <label htmlFor="pw1" style={{ display: "block", fontSize: 13, color: C.textMuted, marginBottom: 6 }}>New password</label>
        <input id="pw1" type="password" autoComplete="new-password" value={pass} onChange={(e) => setPass(e.target.value)} style={input} autoFocus />
        <label htmlFor="pw2" style={{ display: "block", fontSize: 13, color: C.textMuted, marginBottom: 6 }}>Confirm new password</label>
        <input id="pw2" type="password" autoComplete="new-password" value={pass2} onChange={(e) => setPass2(e.target.value)} style={input} />
        {error && <p role="alert" style={{ color: C.red, fontSize: 14, margin: "4px 0 12px" }}>{error}</p>}
        <button type="submit" disabled={busy || !pass || !pass2} style={{
          width: "100%", padding: "13px 0", fontSize: 16, fontWeight: 600, borderRadius: 10, border: "none",
          background: C.green, color: C.bg, cursor: busy ? "default" : "pointer", opacity: busy || !pass || !pass2 ? 0.6 : 1,
        }}>
          {busy ? "Saving…" : "Update password"}
        </button>
      </form>
    </Shell>
  );
}

function VerifyEmail({ token }) {
  const C = useTheme();
  const [state, setState] = useState({ status: "working", message: "" });

  useEffect(() => {
    post("/auth/verify-email", { token }).then(({ ok, data }) =>
      setState(ok
        ? { status: "ok", message: "Your email address is confirmed." }
        : { status: "error", message: errorMessage(data, "This link is invalid or has already been used.") }));
  }, [token]);

  return (
    <Shell title={state.status === "ok" ? "Email confirmed" : state.status === "error" ? "Couldn't confirm" : "Confirming…"}>
      <p style={{ color: state.status === "error" ? C.red : C.textMuted, lineHeight: 1.55 }}>{state.message}</p>
    </Shell>
  );
}

export default function AccountLinkPage({ kind }) {
  const C = useTheme();
  const token = new URLSearchParams(window.location.search).get("token");
  if (!token) {
    return (
      <Shell title="This link is incomplete">
        <p style={{ color: C.textMuted, lineHeight: 1.55 }}>
          Open the link straight from the email, or request a new one from the sign-in screen.{" "}
          <a href={LANDING_URL} style={{ color: C.green }}>followtheleader.app</a>
        </p>
      </Shell>
    );
  }
  return kind === "reset" ? <ResetPassword token={token} /> : <VerifyEmail token={token} />;
}
